import { ArraySortFn, DictionarySortFn } from './internal'

/**
 * Default sort for numbers
 */
export function _sort<T extends (number | string)>(collection: T[]): T[];

/**
 * Simple array sort (wraps native Array#sort). If asc is true it will sort in ascending order.
 */
export function _sort<T>(collection: Array<T>, sortFn: ArraySortFn<T>, asc?: boolean): T[];

/**
 * Object pick sort - pick the given key (must be a number) from each object in the given array and sort based on that.
 * Note: If you are getting a type error that says your sortFn (Key) is not assignable to 'never' it means the key does
 * not refer to a field of type 'number'. This is necessary for this call signature.
 */
export function _sort<T, K extends keyof T>(collection: Array<T>, sortFn: K, asc: T[K] extends number ? boolean : never): T[];

/**
 * Object pick sort - pick the given key (must be a string) from each object in the given array and sort based on that.
 * Note: If you are getting a type error that says your sortFn (Key) is not assignable to 'never' it means the key does
 * not refer to a field of type 'string'. This is necessary for this call signature.
 */
export function _sort<T, K extends keyof T>(collection: Array<T>, sortFn: K, asc: T[K] extends string ? boolean : never): T[];

/**
 * Object pick sort - pick the given key (must be a Date) from each object in the given array and sort based on that.
 * Note: If you are getting a type error that says your sortFn (Key) is not assignable to 'never' it means the key does
 * not refer to a field of type 'Date'. This is necessary for this call signature.
 */
export function _sort<T, K extends keyof T>(collection: Array<T>, sortFn: K, asc: T[K] extends Date ? boolean : never): T[];

/**
 *  Simple object sort (takes each Key-Value from the given object and sorts based on the value) - Returns an array
 */
export function _sort<T extends object>(collection: T, sortFn: DictionarySortFn<T>): Array<T[keyof T]>;

/**
 * Object pick for lookup objects - pick the given key (must be a number) from each object and sort based on that.
 * (Objects that are found at each key of the supplied 'collection' object.)
 * Note: If you are getting a type error that says your sortFn (Key) is not assignable to 'never' it means the key does
 * not refer to a field of type 'number'. This is necessary for this call signature.
 */
export function _sort<T extends object, K extends keyof T[keyof T]>(collection: T, sortFn: K, asc: T[keyof T][K] extends number ? boolean : never): Array<T[keyof T]>;

/**
 * Object pick for lookup objects - pick the given key (must be a string) from each object and sort based on that.
 * (Objects that are found at each key of the supplied 'collection' object.)
 * Note: If you are getting a type error that says your sortFn (Key) is not assignable to 'never' it means the key does
 * not refer to a field of type 'string'. This is necessary for this call signature.
 */
export function _sort<T extends object, K extends keyof T[keyof T]>(collection: T, sortFn: K, asc: T[keyof T][K] extends string ? boolean : never): Array<T[keyof T]>;

/**
 * Object pick for lookup objects - pick the given key (must be a Date) from each object and sort based on that.
 * (Objects that are found at each key of the supplied 'collection' object.)
 * Note: If you are getting a type error that says your sortFn (Key) is not assignable to 'never' it means the key does
 * not refer to a field of type 'Date'. This is necessary for this call signature.
 */
export function _sort<T extends object, K extends keyof T[keyof T]>(collection: T, sortFn: K, asc: T[keyof T][K] extends Date ? boolean : never): Array<T[keyof T]>;

/**
 * Null | undefined will return an empty array
 * Note: If you are getting a type error that says your collection is not assignable to null or undefined and you
 * are attempting to use the object pick version (using an object key as the sort function). It is likely you have
 * forgotten the third argument (ascending: boolean) which is necessary for that call signature.
 */
export function _sort<T>(collection: undefined | null, sortFn: any): T[];

/**
 * Override implementation.
 */
export function _sort<T>(collection: any, sortFn?: any, asc?: boolean): T[] {
	if (!collection) return [] // Don't try to simplify this - Remember typeof null === 'object'

	if (!Array.isArray(collection)) {
		if (typeof collection !== 'object') return []
		collection = Object.keys(collection).map(k => collection[k])
	} else {
		collection = [...collection]
	}

	if (!sortFn) {
		return mergeSort(collection,)
	} else if (typeof sortFn === 'function') {
		return asc
			? mergeSort<T>(collection, sortFn).reverse()
			: mergeSort<T>(collection, sortFn)
	} else {
		if (collection.length > 0 && typeof(collection[0][sortFn]) === 'string') {
			return mergeSort(collection, (a, b) => {
				if (a[sortFn] === b[sortFn]) return 0

				const result = a[sortFn] > b[sortFn] ? 1 : -1
				return result * (asc ? 1 : -1)
			})
		}
		return mergeSort(collection, (a, b) => (a[sortFn] - b[sortFn]) * (asc ? 1 : -1))
	}
}

function mergeSort<T>(arr: T[], comparator?: ArraySortFn<T>): T[] {
	if (!comparator) return [...arr].sort()

	if (arr.length < 2) return [ ...arr ]

	const middle = Math.floor(arr.length / 2)
	const left = arr.slice(0, middle)
	const right = arr.slice(middle)

	return merge(mergeSort(left, comparator), mergeSort(right, comparator), comparator)
}

function merge<T>(left: T[], right: T[], comparator: ArraySortFn<T>): T[] {
	const result = new Array(left.length + right.length)
	let indexLeft = 0
	let indexRight = 0
	let indexResult = 0

	while (indexLeft < left.length && indexRight < right.length) {
		result[indexResult++] = comparator(left[indexLeft], right[indexRight]) > 0
			? right[indexRight++]
			: left[indexLeft++]
	}

	result.length = indexResult

	return [...result, ...left.slice(indexLeft), ...right.slice(indexRight) ]
}
